import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Box, Collapse, Drawer, IconButton, Tooltip, useMediaQuery } from '@mui/material';
import { ChevronDown, ChevronRight, X, Layers3, PanelLeftClose } from 'lucide-react';
import Brand, { BrandMark } from 'components/logo/Brand';
import menuItems from 'menu-items';
import { hasAnyPermission } from 'utils/permissions';
import { DRAWER_WIDTH, MINI_DRAWER_WIDTH } from 'config';
import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';

export default function MainDrawer() {
  const { pathname } = useLocation();
  const mobile = useMediaQuery('(max-width:1023px)');
  const { menuMaster } = useGetMenuMaster();
  const open = Boolean(menuMaster?.isDashboardDrawerOpened);
  const canHover = useMediaQuery('(hover: hover) and (pointer: fine)');
  const [hovered, setHovered] = useState(false);
  const suppressHover = useRef(false);
  const collapseMenu = () => {
    suppressHover.current = true;
    setHovered(false);
    handlerDrawerOpen(false);
  };
  const expanded = mobile || open || hovered;
  const [groups, setGroups] = useState({});
  const items = menuItems.items.flatMap((group) => group.children || []).filter((item) => hasAnyPermission(item.permission));
  const active = (item) =>
    item.url === '/' ? pathname === '/' || pathname === '/default' : pathname === item.url || pathname.startsWith(`${item.url}/`);
  useEffect(() => {
    setHovered(false);
    if (mobile) handlerDrawerOpen(false);
  }, [pathname, mobile]);
  const content = (
    <Box className="xc-sidebar-content">
      <div className="xc-sidebar-brand">
        <Link to="/" aria-label="XchanEcom dashboard">
          {expanded ? <Brand inverse compact /> : <BrandMark size={34} />}
        </Link>
        {mobile && (
          <IconButton onClick={collapseMenu} aria-label="Close navigation" sx={{ color: '#b8cee9' }}>
            <X size={19} />
          </IconButton>
        )}
      </div>
      {expanded && (
        <div className="xc-workspace">
          <span className="xc-workspace-icon">
            <Layers3 size={18} />
          </span>
          <div>
            <strong>Your workspace</strong>
            <span>Dropshipping operations</span>
          </div>
        </div>
      )}
      <nav className="xc-side-links" aria-label="Main navigation">
        {expanded && <div className="xc-nav-caption">WORKSPACE</div>}
        {items.map((item) => {
          const Icon = item.icon;
          const children = item.children?.filter((child) => hasAnyPermission(child.permission));
          if (children && children.length === 0) return null;
          const selected = children ? children.some(active) : active(item);
          const show = groups[item.id] ?? selected;
          const contents = (
            <>
              <span className="xc-nav-icon">{Icon && <Icon sx={{ fontSize: 20 }} />}</span>
              {expanded && (
                <>
                  <span>{item.title === 'Dashboard' ? 'Overview' : item.title}</span>
                  {children && <ChevronDown size={15} className={show ? 'xc-chevron-open' : ''} />}
                </>
              )}
            </>
          );
          return (
            <div key={item.id} className="xc-nav-group">
              <Tooltip title={expanded ? '' : item.title} placement="right">
                {children ? (
                  <button
                    type="button"
                    className={`xc-nav-link ${selected ? 'is-active' : ''}`}
                    aria-label={item.title}
                    aria-expanded={expanded && show}
                    aria-controls={`nav-${item.id}`}
                    onClick={() => {
                      if (!expanded) handlerDrawerOpen(true);
                      setGroups((current) => ({ ...current, [item.id]: !expanded || !show }));
                    }}
                  >
                    {contents}
                  </button>
                ) : (
                  <Link
                    className={`xc-nav-link ${selected ? 'is-active' : ''}`}
                    aria-current={selected ? 'page' : undefined}
                    aria-label={item.title}
                    to={item.url}
                  >
                    {contents}
                  </Link>
                )}
              </Tooltip>
              {children && (
                <Collapse in={expanded && show}>
                  <div id={`nav-${item.id}`} className="xc-subnav">
                    {children.map((child) => (
                      <Link
                        key={child.id}
                        to={child.url}
                        className={active(child) ? 'is-active' : ''}
                        aria-current={active(child) ? 'page' : undefined}
                      >
                        <span className="xc-subnav-dot" />
                        {child.title === 'Fullfillment Partners' ? 'Fulfillment partners' : child.title}
                        <ChevronRight size={12} />
                      </Link>
                    ))}
                  </div>
                </Collapse>
              )}
            </div>
          );
        })}
      </nav>
      <div className="xc-sidebar-bottom">
        {expanded ? (
          <>
            <span className="xc-sidebar-note">Built for connected growth.</span>
            <button
              type="button"
              className="xc-collapse-button"
              onClick={collapseMenu}
              aria-label={mobile ? 'Close navigation' : 'Collapse navigation'}
            >
              <PanelLeftClose size={16} /> {mobile ? 'Close menu' : 'Collapse menu'}
            </button>
          </>
        ) : (
          <span className="xc-sidebar-monogram" aria-hidden="true">
            X
          </span>
        )}
      </div>
    </Box>
  );
  return (
    <Drawer
      variant={mobile ? 'temporary' : 'permanent'}
      open={mobile ? open : true}
      onClose={() => handlerDrawerOpen(false)}
      ModalProps={{ keepMounted: true }}
      slotProps={{
        paper: {
          onMouseEnter: () => { if (!mobile && canHover && !suppressHover.current) setHovered(true); },
          onMouseLeave: () => { suppressHover.current = false; setHovered(false); }
        }
      }}
      sx={{
        '& .MuiDrawer-paper': {
          width: expanded ? DRAWER_WIDTH : MINI_DRAWER_WIDTH,
          bgcolor: '#0b2345',
          color: '#cfdef1',
          border: 0,
          boxShadow: !mobile && hovered && !open ? '10px 0 30px #0b23452b' : 'none',
          transition: 'width .2s ease',
          overflowX: 'hidden',
          '@media(prefers-reduced-motion: reduce)': { transition: 'none' }
        }
      }}
    >
      {content}
    </Drawer>
  );
}
